'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion, cubicBezier } from 'framer-motion'
import ThemeIcon from '@/components/ui/ThemeIcon'
import { cn } from '@/utilities/ui'

export interface FloatingNotifierProps {
  /** Heading text inside the message card */
  title?: string
  /** Body text inside the message card */
  message?: string
  /** Primary button label */
  ctaLabel?: string
  /** Primary button href; if omitted, CTA is hidden */
  ctaHref?: string
  /** Open the CTA in a new tab (adds target=_blank rel=noopener noreferrer) */
  ctaNewTab?: boolean
  /** Whether auto-open should also trigger on first scroll (default true) */
  openOnScroll?: boolean
  /** Kept for backwards compatibility with the server wrapper; currently unused. */
  campaignId?: string
  /** Optional className overrides for the container */
  className?: string
}

/**
 * FloatingNotifier – a bottom-right floating "Störer" widget that reveals a message panel.
 * It displays a circular logo button when closed; clicking opens a card with content.
 *
 * Visibility gate: the widget (chip + card) only appears after the user has
 * interacted with the cookie consent banner. Listens for the custom
 * `lm:cookie-consent-given` event dispatched by the CookieConsent config,
 * and probes `window.CookieConsent.validConsent()` on mount for returning
 * visitors where consent fires synchronously during CC init.
 *
 * Auto-open heuristics (same on mobile and desktop, once consent is given):
 *   - Scroll past 25% of the viewport AND at least 3s have passed since mount
 *   - Auto-opened cards auto-minimize back to the chip after 15s of inactivity
 */
const SCROLL_THRESHOLD_RATIO = 0.25
const MIN_DWELL_MS = 3000
const AUTO_MINIMIZE_MS = 15000
const LEGACY_STORAGE_KEY_PREFIX = 'floating-notifier-dismissed'

export default function FloatingNotifier({
  title = 'WAS WIR BIETEN',
  message = 'Nach Ihren Vorstellungen realisieren wir individuelle Produkte aus Glas und Metall auf Mass. Und auch bei Tür und Tor lassen wir keine Wünsche offen.',
  ctaLabel = 'JETZT ANRUFEN',
  ctaHref,
  ctaNewTab = false,
  openOnScroll = true,
  className = '',
}: FloatingNotifierProps) {
  const [isOpen, setIsOpen] = useState(false)
  // Gate for cookie-consent interaction. Widget stays hidden until the user
  // has interacted with the cookie consent banner.
  const [consentGiven, setConsentGiven] = useState(false)
  const hasAutoOpenedRef = useRef(false)
  const autoOpenedRef = useRef(false)
  const mountedAtRef = useRef<number>(0)
  const autoMinimizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // One-time cleanup of legacy `floating-notifier-dismissed[:*]` keys that
  // permanently hid the widget for users who tapped the old "dismiss" X.
  // The X is now a simple close; the chip should always stay available.
  useEffect(() => {
    try {
      Object.keys(window.localStorage)
        .filter((k) => k.startsWith(LEGACY_STORAGE_KEY_PREFIX))
        .forEach((k) => window.localStorage.removeItem(k))
    } catch {
      // ignore
    }
  }, [])

  // Watch for cookie-consent interaction. Handles both cases:
  //   1. Returning visitor: CC fires onConsent synchronously during init,
  //      possibly before this effect runs. Probe validConsent() on mount +
  //      poll briefly as a safety net.
  //   2. First visit: listen for the custom event dispatched by the CC config.
  useEffect(() => {
    type CCApi = { validConsent?: () => boolean }
    const readConsent = (): boolean => {
      const cc = (window as unknown as { CookieConsent?: CCApi }).CookieConsent
      return Boolean(cc?.validConsent?.())
    }

    if (readConsent()) {
      setConsentGiven(true)
      return
    }

    const onConsent = () => setConsentGiven(true)
    window.addEventListener('lm:cookie-consent-given', onConsent)

    // Safety net: CC may not have attached the API by mount time.
    const pollId = window.setInterval(() => {
      if (readConsent()) setConsentGiven(true)
    }, 500)
    const stopPollId = window.setTimeout(() => window.clearInterval(pollId), 15000)

    return () => {
      window.removeEventListener('lm:cookie-consent-given', onConsent)
      window.clearInterval(pollId)
      window.clearTimeout(stopPollId)
    }
  }, [])

  // Capture mount timestamp for the dwell-time gate — start counting from
  // when consent was given, not from component mount.
  useEffect(() => {
    if (consentGiven) mountedAtRef.current = Date.now()
  }, [consentGiven])

  // Auto-open on scroll past 25% of viewport AND at least 3s dwell.
  // Same behavior on mobile and desktop, gated on consent being given.
  useEffect(() => {
    if (!openOnScroll || !consentGiven || hasAutoOpenedRef.current) return

    const onScroll = () => {
      if (hasAutoOpenedRef.current) return
      const scrolledEnough = window.scrollY > window.innerHeight * SCROLL_THRESHOLD_RATIO
      const dwelledEnough = Date.now() - mountedAtRef.current >= MIN_DWELL_MS
      if (scrolledEnough && dwelledEnough) {
        hasAutoOpenedRef.current = true
        autoOpenedRef.current = true
        setIsOpen(true)
        window.removeEventListener('scroll', onScroll)
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [openOnScroll, consentGiven])

  const clearAutoMinimizeTimer = useCallback(() => {
    if (autoMinimizeTimerRef.current) {
      clearTimeout(autoMinimizeTimerRef.current)
      autoMinimizeTimerRef.current = null
    }
  }, [])

  // Start 15s auto-minimize timer when an auto-opened card is showing
  useEffect(() => {
    if (!isOpen || !autoOpenedRef.current) return
    autoMinimizeTimerRef.current = setTimeout(() => {
      setIsOpen(false)
      autoOpenedRef.current = false
    }, AUTO_MINIMIZE_MS)
    return () => {
      if (autoMinimizeTimerRef.current) {
        clearTimeout(autoMinimizeTimerRef.current)
        autoMinimizeTimerRef.current = null
      }
    }
  }, [isOpen])

  const open = useCallback(() => {
    // User-initiated open — no auto-minimize
    autoOpenedRef.current = false
    clearAutoMinimizeTimer()
    setIsOpen(true)
  }, [clearAutoMinimizeTimer])

  const close = useCallback(() => {
    // User-initiated close — reset auto-open flag so a future re-open is "user-opened"
    autoOpenedRef.current = false
    clearAutoMinimizeTimer()
    setIsOpen(false)
  }, [clearAutoMinimizeTimer])

  const containerClasses = useMemo(
    () =>
      cn(
        'fixed z-50 bottom-6 right-6 sm:bottom-8 sm:right-8 flex flex-col-reverse items-end gap-3', // positioning - vertical stack (message card on top)
        className,
      ),
    [className],
  )

  // Animation presets aligned with project ease
  const ease = cubicBezier(0.25, 0.46, 0.45, 0.94)

  if (!consentGiven) return null

  return (
    <div className={containerClasses}>
      {/* Circle Logo Button */}
      <button
        type="button"
        aria-label={isOpen ? 'Nachricht schließen' : 'Nachricht öffnen'}
        onClick={isOpen ? close : open}
        className={cn(
          'shrink-0 inline-flex items-center justify-center rounded-full',
          'bg-card shadow-md shadow-[var(--color-shadow)]/30',
          'border border-border-brand/20',
          'size-12 sm:size-14',
        )}
      >
        <ThemeIcon size="size-7 sm:size-8" alt="Lüchinger Metallbau Logo" ariaHidden={false} />
      </button>

      {/* Message Card */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.4, ease }}
            onClick={clearAutoMinimizeTimer}
            onPointerDown={clearAutoMinimizeTimer}
            className={cn(
              'relative origin-bottom-right w-[min(75vw,20rem)] sm:w-[28rem]',
              'bg-card border border-border-brand/30 shadow-xl',
              // Arrow triangle pointing to button (after pseudo-element) - centered on button
              'after:absolute after:-bottom-3 after:right-[0.75rem] sm:after:right-[1rem]',
              'after:w-0 after:h-0',
              'after:border-l-[12px] after:border-l-transparent',
              'after:border-r-[12px] after:border-r-transparent',
              'after:border-t-[12px] after:border-t-card',
              // Border effect for the triangle (before pseudo-element)
              'before:absolute before:-bottom-[14px] before:right-[calc(0.75rem-1px)] sm:before:right-[calc(1rem-1px)]',
              'before:w-0 before:h-0',
              'before:border-l-[13px] before:border-l-transparent',
              'before:border-r-[13px] before:border-r-transparent',
              'before:border-t-[13px] before:border-t-border-brand/30',
              'before:-z-10',
            )}
          >
            {/* Close — collapses the card back to the chip; does not permanently dismiss */}
            <button
              type="button"
              aria-label="Nachricht schließen"
              onClick={close}
              className="absolute top-3 right-3 p-1 hover:bg-brand/10 hover:text-brand transition-colors"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 6L18 18M6 18L18 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="min-w-0">
                  <h3 className="heading-sm text-heading tracking-tight">{title}</h3>
                  <p className="mt-2 text-body subtitle-sm leading-relaxed whitespace-pre-line">
                    {message}
                  </p>
                </div>
              </div>

              {ctaHref && (
                <div className="mt-5">
                  <a
                    href={ctaHref}
                    {...(ctaNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className={cn(
                      'inline-flex items-center px-4 py-2',
                      'bg-brand text-text-white hover:opacity-90 transition-opacity label-md',
                    )}
                  >
                    {ctaLabel}
                  </a>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
